<template>
  <div class="docs-listing__container">
    <div class="filter">
      <div v-if="view" class="filter__container">
        <h2 class="filter__title">
          {{ view.filterTitle() }}
        </h2>
        <CustomListMenu v-if="showCustomListMenu" :list="view.customList" />
        <div v-if="view.docs().isDocSortVisible()" class="filter__box">
          <DocsSort :view="view.docs()" />
        </div>
      </div>
    </div>

    <!-- Main data -->
    <div v-if="view.dataLength" class="docs-listing__list">
      <ListCard
        v-for="doc in view.docs().filteredDocs"
        :key="doc.id"
        :view="view"
        :doc="doc"
        @click="trackDocView($event, doc)"
      />
    </div>

    <div v-if="view && !view.dataLength">
      <CustomListEmpty v-if="view.customList?.doc_type === 'custom_list'" />
      <div v-else class="text">
        {{ view.notFoundMessage() }}
      </div>
    </div>

    <DocsSortMobile v-if="view.docs().isDocSortVisible()" :view="view.docs()" />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
} from 'vue'

import CustomListMenu from '@/app/components/custom.lists/CustomListMenu.vue'
import CustomListEmpty from '@/app/components/custom.lists/CustomListEmpty.vue'
import ListCard from '@/app/components/listing/ListCard.vue'
import DocsSort from '@/app/components/listing/DocsSort.vue'
import DocsSortMobile from '@/app/components/listing/DocsSort.mobile.vue'

import { onBeforeRouteLeave, useRouter } from 'vue-router'

import { ListView } from '@/models/docs.list'
import { DocsScroll } from '@/models/docs.scroll'
import { Mixpanel } from '@/analytics/mixpanel'
import { assertNotNull } from '@/helpers/typing'
import { ListItem } from '@/models/interfaces'

const props = defineProps<{
  view: ListView
}>()

const router = useRouter()

const docsScroll = ref<DocsScroll | null>(null)

const lastScrollPosition = ref<number | null>(null)

onMounted(() => {
  if (props.view) {
    docsScroll.value = new DocsScroll(props.view)
  }
})

onActivated(async () => {
  // Start the event listeners if they haven't started already.
  if (!docsScroll.value?.isListening) {
    docsScroll.value?.initEventListeners()
  }

  // If there's a stored scroll position, restore it.
  if (lastScrollPosition.value) {
    await nextTick()
    appEl().scrollTop = lastScrollPosition.value
  }
})

onBeforeRouteLeave(() => {
  // Store the scroll position when going to a new page, so it can be
  // restored later when this component is restored.
  //
  // We do this `onBeforeRouteLeave` instead of `onDeactivated` because
  // `onDeactivated` only triggers after the component has already left
  // the DOM, so we can't get the scroll position.
  lastScrollPosition.value = appEl().scrollTop
})

onDeactivated(() => {
  docsScroll.value?.removeScrollEventListener()
})

onBeforeUnmount(() => {
  if (docsScroll.value) {
    docsScroll.value.removeScrollEventListener()
  }
})

/**
 * Get the app container element.
 */
function appEl(): HTMLElement {
  return assertNotNull(document.getElementById('app'))
}

function trackDocView(event: Event, doc: ListItem): void {
  try {
    Mixpanel.trackDocView(
      event,
      props.view.filterTitle(),
      router.currentRoute.value.fullPath,
      doc,
    )
  } catch (error) {
    // Unexpected error in the Mixpanel tracking
    console.error(error)
    return
  }
}

/**
 * Return whether or not the current page is a non-standard Custom List listing page.
 */
const showCustomListMenu = computed(() => {
  if (!props.view.customList) {
    return false
  }

  const isCustomList = props.view.customList.doc_type === 'custom_list'
  const isStandard = props.view.customList.is_standard

  return isCustomList && !isStandard
})
</script>

<style lang="scss">
.docs-listing__list {
  --docs-listing-columns-desktop: 4;
  display: grid;
  grid-template-columns: repeat(var(--docs-listing-columns-desktop), 1fr);
  gap: 24px;
  @media only screen and (max-width: $w-tablet-s) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  @media only screen and (max-width: $w-tablet-s) {
    gap: 13px;
  }
}
</style>
